import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import MarketingNav from "../../components/MarketingNav";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";
import DigestSignup from "../../components/DigestSignup";
import RelatedPosts from "../../components/RelatedPosts";
import { bySlug, posts, relatedPosts } from "../../content/blog";
import { Calendar, Clock, ArrowLeft, ArrowRight, Share2 } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const post = bySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      "@type": "Person",
      name: post.author,
      jobTitle: post.author_role,
    },
    publisher: {
      "@type": "Organization",
      name: "Lunar Occultation, Inc.",
      logo: {
        "@type": "ImageObject",
        url: "https://lunaroccultation.com/favicon.svg",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://lunaroccultation.com/blog/${post.slug}`,
    },
    description: post.excerpt,
    keywords: post.keywords,
    image: "https://lunaroccultation.com/og-image.svg",
  };

  const idx = posts.findIndex((p) => p.slug === slug);
  const next = posts[idx + 1] || posts[0];
  const related = relatedPosts(slug, 3);

  return (
    <div>
      <SEO
        path={`/blog/${post.slug}`}
        title={post.title}
        description={post.excerpt}
        keywords={post.keywords}
        type="article"
        schema={schema}
      />
      <MarketingNav />

      <article className="container-x pt-14 pb-20 max-w-3xl mx-auto">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-navy/60 hover:text-[#FF6B35]" data-testid="blog-back">
          <ArrowLeft size={14} /> All posts
        </Link>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {post.tags.map((t) => (
            <span key={t} className="chip chip-orange text-[10px]">{t}</span>
          ))}
        </div>

        <h1 data-testid="blog-post-title" className="font-display text-3xl md:text-5xl font-bold text-navy mt-5 leading-[1.08]">{post.title}</h1>

        <div className="mt-6 flex items-center gap-5 text-sm ink border-b border-[rgba(10,14,26,0.08)] pb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF6B35] flex items-center justify-center text-white font-bold">
              {post.author.split(" ").map((n) => n[0]).join("")}
            </div>
            <div>
              <div className="font-semibold text-navy">{post.author}</div>
              <div className="text-[11px] text-navy/50">{post.author_role}</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1"><Calendar size={12} /> {formatDate(post.date)}</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {post.read_minutes} min read</span>
        </div>

        <div className="prose-sentry mt-10">
          {post.body.map((block, i) => renderBlock(block, i))}
        </div>

        {/* Author CTA + share */}
        <div className="mt-14 pt-8 border-t border-[rgba(10,14,26,0.08)] flex items-center justify-between flex-wrap gap-4">
          <div className="text-sm ink">
            Written by <span className="text-navy font-semibold">{post.author}</span> · {post.author_role}
          </div>
          <button
            type="button"
            data-testid="blog-share"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(window.location.href);
                alert("Link copied to clipboard");
              } catch { /* noop */ }
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-navy text-cream hover:bg-black"
          >
            <Share2 size={12} /> Copy share link
          </button>
        </div>
      </article>

      {/* Related posts — tag-based "keep reading" */}
      <section className="container-x max-w-5xl mx-auto">
        <RelatedPosts items={related} />
      </section>

      {/* Digest email capture */}
      <section className="container-x max-w-3xl mx-auto pb-20">
        <DigestSignup source={`/blog/${post.slug}`} />
      </section>

      <Footer />
    </div>
  );
}

function renderBlock(block, i) {
  switch (block.t) {
    case "h2":
      return <h2 key={i} className="font-display text-2xl md:text-3xl font-bold text-navy mt-12 mb-4">{block.text}</h2>;
    case "h3":
      return <h3 key={i} className="font-display text-xl font-bold text-navy mt-8 mb-3">{block.text}</h3>;
    case "p":
      return <p key={i} className="ink text-base md:text-lg leading-relaxed mb-5">{renderInline(block.text)}</p>;
    case "list":
      return (
        <ul key={i} className="list-disc list-outside pl-6 space-y-2 ink text-base mb-6 marker:text-[#FF6B35]">
          {block.items.map((it, j) => <li key={j} className="leading-relaxed">{renderInline(it)}</li>)}
        </ul>
      );
    case "code":
      return (
        <pre key={i} className="bg-navy text-cream rounded-xl p-5 font-mono text-[13px] leading-relaxed overflow-x-auto my-6 border border-[rgba(10,14,26,0.1)]">
          <code>{block.text}</code>
        </pre>
      );
    case "note":
      return (
        <blockquote key={i} className="my-7 rounded-xl bg-gradient-to-br from-[rgba(255,107,53,0.12)] to-[rgba(255,217,138,0.08)] border-l-4 border-[#FF6B35] p-5">
          <div className="eyebrow text-[#FF6B35] mb-2" style={{ fontSize: 10 }}>CALLOUT</div>
          <div className="text-navy text-lg font-display font-semibold leading-snug">{block.text}</div>
        </blockquote>
      );
    default:
      return null;
  }
}

// Minimal inline formatter: handles `code` segments.
function renderInline(text) {
  if (!text || typeof text !== "string") return text;
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith("`") && p.endsWith("`")) {
      return <code key={i} className="font-mono text-[0.9em] bg-[#EFEAE1] text-navy px-1.5 py-0.5 rounded">{p.slice(1, -1)}</code>;
    }
    return p;
  });
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}
